/**
 * Grid Component
 * Box with display: grid for CSS grid layouts
 */

import React, { forwardRef } from 'react';
import { Box } from './Box';

/**
 * Grid Component
 * A Box with display="grid", supporting all grid-related style props
 * 
 * @example
 * <Grid templateColumns="repeat(3, 1fr)" gap={6}>
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 *   <Box>Item 3</Box>
 * </Grid>
 */
export const Grid = forwardRef(({
  templateColumns,
  gridTemplateColumns,
  templateRows,
  gridTemplateRows,
  templateAreas,
  gridTemplateAreas,
  column,
  gridColumn,
  row,
  gridRow,
  area,
  gridArea,
  autoFlow,
  gridAutoFlow,
  autoRows,
  gridAutoRows,
  autoColumns,
  gridAutoColumns,
  gap,
  gridGap,
  rowGap,
  gridRowGap,
  columnGap,
  gridColumnGap,
  ...props
}, ref) => {
  return (
    <Box
      ref={ref}
      display="grid"
      gridTemplateColumns={templateColumns || gridTemplateColumns}
      gridTemplateRows={templateRows || gridTemplateRows}
      gridTemplateAreas={templateAreas || gridTemplateAreas}
      gridColumn={column || gridColumn}
      gridRow={row || gridRow}
      gridArea={area || gridArea}
      gridAutoFlow={autoFlow || gridAutoFlow}
      gridAutoRows={autoRows || gridAutoRows}
      gridAutoColumns={autoColumns || gridAutoColumns}
      gap={gap || gridGap}
      rowGap={rowGap || gridRowGap}
      columnGap={columnGap || gridColumnGap}
      {...props}
    />
  );
});

Grid.displayName = 'Grid';

/**
 * GridItem Component
 * Wrapper for items in a Grid
 */
export const GridItem = forwardRef(({
  colSpan,
  rowSpan,
  colStart,
  colEnd,
  rowStart,
  rowEnd,
  area,
  ...props
}, ref) => {
  const gridColumnValue = colSpan ? `span ${colSpan} / span ${colSpan}` : undefined;
  const gridRowValue = rowSpan ? `span ${rowSpan} / span ${rowSpan}` : undefined;

  return (
    <Box
      ref={ref}
      gridColumn={gridColumnValue}
      gridRow={gridRowValue}
      gridColumnStart={colStart}
      gridColumnEnd={colEnd}
      gridRowStart={rowStart}
      gridRowEnd={rowEnd}
      gridArea={area}
      {...props}
    />
  );
});

GridItem.displayName = 'GridItem';

export default Grid;
